import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import api from "../lib/api";
import { US_LOCATIONS, OTHER_LOCATION } from "../lib/locations";
import AppleSignInButton from "../components/AppleSignInButton";
import GoogleIcon from "../components/GoogleIcon";

export default function Signup() {
  const { user, register, googleLogin } = useAuth();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const nextPath = searchParams.get("next") || "/workspaces";
  const isSafeNext = typeof nextPath === "string" && nextPath.startsWith("/") && !nextPath.startsWith("//");
  const redirectTo = isSafeNext ? nextPath : "/workspaces";
  // Session 56 · Alpha invite deep-link prefill.
  const invitedEmail = (searchParams.get("invite") || "").toLowerCase().trim();
  const inviteCohort = (searchParams.get("cohort") || "").trim();
  const [form, setForm] = useState({
    name: "",
    email: invitedEmail,
    password: "",
    city: "Washington, DC",
    postal_code: "",
    resolved_city: "",
    state: "",
    state_code: "",
    profession_type: "",
  });
  // Session 58 · Two-field UX: state dropdown + optional country input when
  // "Other (outside US)" is picked. Whichever value is active flows into
  // `form.city` on submit so the backend contract doesn't change.
  const [locationChoice, setLocationChoice] = useState("Washington, DC");
  const [otherCountry, setOtherCountry] = useState("");
  // Session 60.18 · ZIP auto-detect status
  const [zipStatus, setZipStatus] = useState({ state: "idle", label: "" });
  const [professions, setProfessions] = useState([]);
  // Session 55 · TWO separate legal acknowledgments per spec.
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptPrivacy, setAcceptPrivacy] = useState(false);
  const [legal, setLegal] = useState(null);   // {terms: {version, effective_date}, privacy: {...}}
  const [err, setErr] = useState("");
  const [appleErr, setAppleErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/options/professions").then(r => setProfessions(r.data)).catch(() => {});
    api.get("/legal/current").then(r => setLegal(r.data.documents)).catch(() => {});
  }, []);

  if (user) return <Navigate to={redirectTo} replace />;

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // Session 60.18 · ZIP lookup — fires as soon as the input is 5 digits.
  // Populates `state` (dropdown value), `resolved_city`, `state_code` on the
  // form so the payload carries everything on submit. Never blocks signup
  // if the lookup fails.
  const onZipChange = async (e) => {
    const raw = e.target.value.replace(/[^0-9]/g, "").slice(0, 5);
    setForm((f) => ({ ...f, postal_code: raw }));
    if (raw.length !== 5) {
      setZipStatus({ state: "idle", label: "" });
      return;
    }
    setZipStatus({ state: "looking", label: "Detecting..." });
    try {
      const r = await api.get(`/geo/zip-lookup?zip=${raw}`);
      const { city, state, state_code } = r.data;
      setForm((f) => ({
        ...f,
        resolved_city: city || "",
        state: state || "",
        state_code: state_code || "",
        // If the resolved state is a US_LOCATIONS entry, snap the dropdown.
        city: (state && US_LOCATIONS.includes(state)) ? state : f.city,
      }));
      if (state && US_LOCATIONS.includes(state)) {
        setLocationChoice(state);
      }
      setZipStatus({ state: "ok", label: `${city}, ${state_code || state}` });
    } catch {
      setZipStatus({ state: "miss", label: "No match — pick a location below" });
    }
  };

  const legalReady = Boolean(legal?.terms?.version && legal?.privacy?.version);
  const canSubmit = acceptTerms && acceptPrivacy && legalReady && !busy;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!acceptTerms || !acceptPrivacy) {
      setErr("Please accept the Terms of Service and Privacy Policy to continue.");
      return;
    }
    if (!legalReady) {
      setErr("Legal documents haven't loaded yet — please try again in a moment.");
      return;
    }
    setErr(""); setBusy(true);
    try {
      await register({
        ...form,
        accepted_terms: true,
        accepted_privacy: true,
        accepted_terms_version: legal.terms.version,
        accepted_privacy_version: legal.privacy.version,
        invite_email: invitedEmail || null,
        cohort: inviteCohort || null,
      });
      nav(redirectTo);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Registration failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="m-auth-page">
      <div className="m-auth-aside">
        <div className="m-auth-logo-wrap">
          <img src="/brand/metier-logo-gold.png" alt="Métier" />
        </div>
        <div>
          <span className="m-eyebrow">Membership</span>
          <h1 className="m-auth-heading">Work <em>Anywhere</em>.<br/>Connect <em>Everywhere</em>.</h1>
          <p className="m-auth-lede">A curated network of workspaces, Métier Hours café perks, and a community built for professionals with flexible schedules.</p>
        </div>
        <div className="m-auth-foot-aside"><span>workmetier.com</span></div>
      </div>
      <div className="m-auth-main">
        <div className="m-auth-card">
          <span className="m-eyebrow">Join</span>
          <h2 className="m-auth-h2">Become a <em>member</em>.</h2>
          <p className="m-auth-sub">Join Métier for free. Sign up to access full member perks, community, and local events.</p>

          {invitedEmail && (
            <div data-testid="invite-banner" style={{
              background:"rgba(184,145,58,0.10)",
              border:"1px solid rgba(184,145,58,0.4)",
              borderRadius:10,
              padding:"12px 14px",
              margin:"6px 0 16px",
              fontSize:13,
              color:"#1B2340",
              lineHeight:1.5,
            }}>
              <div style={{fontSize:11, letterSpacing:"0.14em", textTransform:"uppercase", color:"#B8913A", fontWeight:600, marginBottom:2}}>
                Alpha invitation
              </div>
              You&#39;re invited as <strong>{invitedEmail}</strong>
              {inviteCohort && <> · <span style={{color:"#B8913A"}}>{inviteCohort.toUpperCase()}</span> cohort</>}.
              Complete signup to claim your Founding spot.
            </div>
          )}

          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <AppleSignInButton testid="apple-signup-btn" onError={(m) => setAppleErr(m)} />
            <button type="button" className="m-google-btn" onClick={googleLogin} data-testid="google-signup-btn">
              <GoogleIcon /> <span>Continue with Google</span>
            </button>
          </div>
          {appleErr && <div className="m-form-err" data-testid="signup-apple-error">{appleErr}</div>}
          <div className="m-divider"><span>or with email</span></div>

          <form onSubmit={onSubmit} className="m-form">
            <label className="m-field">
              <span>Full name</span>
              <input value={form.name} onChange={update("name")} required autoComplete="name" data-testid="signup-name" />
            </label>
            <label className="m-field">
              <span>Email</span>
              <input type="email" value={form.email} onChange={update("email")} required autoComplete="email" data-testid="signup-email" />
            </label>
            <label className="m-field">
              <span>Password</span>
              <input type="password" value={form.password} onChange={update("password")} required minLength={6} autoComplete="new-password" data-testid="signup-password" />
            </label>
            <label className="m-field" data-testid="signup-zip-field">
              <span>ZIP code <span style={{ opacity: 0.6, fontWeight: 400 }}>(optional — auto-detects your state)</span></span>
              <input
                type="text"
                inputMode="numeric"
                maxLength={5}
                pattern="[0-9]*"
                value={form.postal_code}
                onChange={onZipChange}
                placeholder="e.g. 17101"
                data-testid="signup-zip"
                autoComplete="postal-code"
              />
              {zipStatus.label && (
                <span
                  data-testid="signup-zip-status"
                  style={{
                    fontSize: 11.5,
                    marginTop: 4,
                    color: zipStatus.state === "ok" ? "var(--gold, #B8913A)" : zipStatus.state === "miss" ? "#B34141" : "rgba(27,35,64,0.55)",
                    fontWeight: 500,
                  }}
                >
                  {zipStatus.state === "ok" ? "\u2713 " : ""}{zipStatus.label}
                </span>
              )}
            </label>
            <label className="m-field">
              <span>Primary location</span>
              <select
                value={locationChoice}
                onChange={(e) => {
                  const val = e.target.value;
                  setLocationChoice(val);
                  if (val !== OTHER_LOCATION) {
                    setForm((f) => ({ ...f, city: val }));
                  } else {
                    setForm((f) => ({ ...f, city: otherCountry }));
                  }
                }}
                data-testid="signup-city"
              >
                {US_LOCATIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                <option value={OTHER_LOCATION}>Other (outside US)…</option>
              </select>
            </label>
            {locationChoice === OTHER_LOCATION && (
              <label className="m-field" data-testid="signup-country-field">
                <span>Country</span>
                <input
                  type="text"
                  value={otherCountry}
                  onChange={(e) => {
                    setOtherCountry(e.target.value);
                    setForm((f) => ({ ...f, city: e.target.value }));
                  }}
                  placeholder="e.g. United Kingdom"
                  required
                  data-testid="signup-country"
                />
              </label>
            )}
            <label className="m-field">
              <span>How do you work?</span>
              <select value={form.profession_type} onChange={update("profession_type")} data-testid="signup-profession">
                <option value="">Prefer not to say</option>
                {professions.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
              </select>
            </label>
            {err && <div className="m-form-err" data-testid="signup-error">{err}</div>}

            <div className="legal-signup-checks" data-testid="signup-legal-block">
              <label>
                <input
                  type="checkbox"
                  checked={acceptTerms}
                  onChange={(e) => setAcceptTerms(e.target.checked)}
                  data-testid="signup-terms-checkbox"
                  aria-describedby="signup-terms-copy"
                />
                <span id="signup-terms-copy">
                  I have read and agree to the{" "}
                  <Link to="/legal/terms" target="_blank" rel="noopener" data-testid="signup-terms-link">
                    Terms of Service
                  </Link>
                  {legal?.terms && <> (v{legal.terms.version})</>}.
                </span>
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={acceptPrivacy}
                  onChange={(e) => setAcceptPrivacy(e.target.checked)}
                  data-testid="signup-privacy-checkbox"
                  aria-describedby="signup-privacy-copy"
                />
                <span id="signup-privacy-copy">
                  I acknowledge the{" "}
                  <Link to="/legal/privacy" target="_blank" rel="noopener" data-testid="signup-privacy-link">
                    Privacy Policy
                  </Link>
                  {legal?.privacy && <> (v{legal.privacy.version})</>}.
                </span>
              </label>
            </div>

            <button type="submit" className="m-btn-gold" disabled={!canSubmit} data-testid="signup-submit">
              {busy ? "Creating account…" : "Create Account"}
            </button>
          </form>

          <p className="m-auth-foot">Already a member? <Link to="/login" data-testid="link-login">Sign in →</Link></p>
        </div>
      </div>
    </div>
  );
}
